import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config/config';
import authHeader from '../config/authHeader';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();

    const [user, setUser] = useState(null);
    const [token, setToken] = useState(sessionStorage.getItem('token') || null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const normalizeRole = (profileNom) => {
        if (!profileNom) return 'visiteur';
        const n = String(profileNom).toLowerCase();
        if (n.includes('admin')) return 'admin';
        if (n.includes('ca')) return 'ca';
        if (n.includes('manager')) return 'manager';
        return n;
    };

    const shallowEqual = (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;
        const ka = Object.keys(a);
        const kb = Object.keys(b);
        if (ka.length !== kb.length) return false;
        for (const k of ka) if (a[k] !== b[k]) return false;
        return true;
    };

    useEffect(() => {
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }

        let intervalId;
        let isMounted = true;
        let lastUserJson = null;

        const loadUser = async (isInitial = false) => {
            try {
                const userData = await fetchUserData();

                if (!isMounted) return;

                if (!userData?.user) {
                    console.warn('User invalid — logging out');
                    logout();
                    return;
                }

                const userJson = JSON.stringify(userData.user);

                if (userJson !== lastUserJson) {
                    lastUserJson = userJson;
                    if (isInitial) {
                        setUser(userData.user);
                    } else {
                        setUser(prev => shallowEqual(prev, userData.user) ? prev : userData.user);
                    }
                }
            } catch (err) {
                console.error('User check failed', err);
            } finally {
                if (isInitial && isMounted) setLoading(false);
            }
        };

        setLoading(true);
        loadUser(true);

        intervalId = setInterval(() => loadUser(false), 10000);

        return () => {
            isMounted = false;
            if (intervalId) clearInterval(intervalId);
        };
    }, [token]);


    const login = useCallback(async (email, password) => {
        setError('');
        try {
            const formData = new FormData();
            formData.append('email', email);
            formData.append('password', password);

            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_AuthLogin`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });

            const data = await response.json();

            if (response.ok && data.accessToken) {
                sessionStorage.setItem('token', data.accessToken);
                setToken(data.accessToken);

                setLoading(true);
                try {
                    const userData = await fetchUserData();
                    setUser(userData?.user ?? null);
                } finally {
                    setLoading(false);
                }
            } else {
                setError(data.response || 'Authentication failed');
            }
        } catch (err) {
            setError('Une erreur est apparue. Rééssayez.');
        }
    });

    const logout = useCallback(() => {
        sessionStorage.removeItem('token');
        setUser(null);
        setToken(null);
        navigate('/');
    });

    const fetchUserData = useCallback(async () => {
        try {
            const userRes = await fetch(`${config.apiBaseUrl}/4DACTION/KST_getUserInfo`, {
                headers: authHeader(),
            });

            if (userRes.status === 401) {
                // token invalide/expiré
                return null; // on laisse PrivateRoute renvoyer au /login
            }

            if (!userRes.ok) {
                console.error('getUserInfo failed with status', userRes.status);
                return null;
            }

            const u = await userRes.json();
            const role = normalizeRole(u?.Role);

            return {
                user: {
                    UUID_: u.UUID_,
                    Nom: u.Nom,
                    Email: u.Email,
                    Role: role,
                    Capabilities: u.Capabilities || [],
                },
            };
        } catch (err) {
            console.error('Erreur globale récupération user data', err);
            return null;
        }
    });

    const registerIntervenant = async ({ inviteToken, password }) => {
        setError('');
        try {
            const formData = new FormData();
            formData.append('inviteToken', inviteToken);
            formData.append('password', password);
            const response = await fetch(`${config.apiBaseUrl}/4DACTION/KST_registerIntervenant`, {
                method: 'POST',
                headers: authHeader(),
                body: formData,
            });
            const data = await response.json();
            if (response.ok) {
                navigate('/login');
            } else {
                setError(data.response || 'Registration failed');
            }
        } catch (err) {
            setError('Une erreur est apparue. Rééssayez.');
        }
    };

    const ctxValue = useMemo(() => ({
        user, token, login, logout, loading, error,
        isLoggedIn: !!token,
        fetchUserData,
        registerIntervenant,
        hasRole: (...roles) => roles.includes(user?.Role),
        hasAny: (...roles) => roles.some(r => r === user?.Role),
        hasAll: (...roles) => roles.every(r => r === user?.Role),
    }), [user, token, login, logout, loading, error, registerIntervenant]);

    return (
        <AuthContext.Provider value={ctxValue}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
